import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAdminClientViewTransportConfig,
  resetAdminClientViewTransportConfig,
  updateAdminClientViewTransportConfig,
  validateAdminClientViewTransportConfig,
} from "../../../api/clientViewTransportApi";
import StatusBadge from "../intelligence/StatusBadge";

const DEFAULT_FORM = {
  isEnabled: true,
  defaultMode: "network",
  layerConfigJson: "{}",
  sourceConfigJson: "{}",
  dashboardConfigJson: "{}",
  scorecardConfigJson: "{}",
  accessScoreConfigJson: "{}",
  reportConfigJson: "{}",
  liveConfigJson: "{}",
  roadConfigJson: "{}",
  disclaimerConfigJson: "{}",
};

function stringifyJson(value) {
  return JSON.stringify(value || {}, null, 2);
}

function ClientViewTransportConfigSection({ clientViewId, canManage }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [schema, setSchema] = useState(null);
  const [effectiveConfig, setEffectiveConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await fetchAdminClientViewTransportConfig(clientViewId);
      const stored = body.storedConfig;
      setSchema(body.schema || null);
      setEffectiveConfig(body.effectiveConfig || null);
      setForm({
        isEnabled: stored?.isEnabled !== false,
        defaultMode: stored?.defaultMode || body.globalDefaults?.defaultMode || "network",
        layerConfigJson: stringifyJson(stored?.layerConfig || {}),
        sourceConfigJson: stringifyJson(stored?.sourceConfig || {}),
        dashboardConfigJson: stringifyJson(stored?.dashboardConfig || {}),
        scorecardConfigJson: stringifyJson(stored?.scorecardConfig || {}),
        accessScoreConfigJson: stringifyJson(stored?.accessScoreConfig || {}),
        reportConfigJson: stringifyJson(stored?.reportConfig || {}),
        liveConfigJson: stringifyJson(stored?.liveConfig || {}),
        roadConfigJson: stringifyJson(stored?.roadConfig || {}),
        disclaimerConfigJson: stringifyJson(stored?.disclaimerConfig || {}),
      });
    } catch (error) {
      setErrorMessage(error.message || "Unable to load Transport configuration.");
    } finally {
      setIsLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const parsePayload = () => {
    const parseField = (text, label) => {
      try {
        return JSON.parse(text);
      } catch {
        throw new Error(`${label} must be valid JSON.`);
      }
    };

    return {
      isEnabled: Boolean(form.isEnabled),
      defaultMode: form.defaultMode,
      layerConfig: parseField(form.layerConfigJson, "Layer config"),
      sourceConfig: parseField(form.sourceConfigJson, "Source config"),
      dashboardConfig: parseField(form.dashboardConfigJson, "Dashboard config"),
      scorecardConfig: parseField(form.scorecardConfigJson, "Scorecard config"),
      accessScoreConfig: parseField(form.accessScoreConfigJson, "Real Estate access config"),
      reportConfig: parseField(form.reportConfigJson, "Community report config"),
      liveConfig: parseField(form.liveConfigJson, "Live operations config"),
      roadConfig: parseField(form.roadConfigJson, "Road config"),
      disclaimerConfig: parseField(form.disclaimerConfigJson, "Disclaimer config"),
    };
  };

  const exampleOptions = useMemo(() => {
    if (!schema?.examples) return [];
    return Object.entries(schema.examples).map(([key, value]) => ({ key, value }));
  }, [schema]);

  const applyExample = (exampleKey) => {
    const example = schema?.examples?.[exampleKey];
    if (!example) return;
    setForm((prev) => ({
      ...prev,
      isEnabled: example.isEnabled !== false,
      defaultMode: example.defaultMode || prev.defaultMode,
      layerConfigJson: stringifyJson(example.layerConfig || {}),
      dashboardConfigJson: stringifyJson(example.dashboardConfig || {}),
      scorecardConfigJson: stringifyJson(example.scorecardConfig || {}),
      accessScoreConfigJson: stringifyJson(example.accessScoreConfig || {}),
      reportConfigJson: stringifyJson(example.reportConfig || {}),
      liveConfigJson: stringifyJson(example.liveConfig || {}),
    }));
    setSuccessMessage(`Loaded ${exampleKey} example — review and save to persist.`);
  };

  const handleValidate = async () => {
    setValidationErrors([]);
    setValidationWarnings([]);
    try {
      const payload = parsePayload();
      const body = await validateAdminClientViewTransportConfig(clientViewId, payload);
      setValidationErrors(body.errors || []);
      setValidationWarnings(body.warnings || []);
      if (body.valid) {
        setSuccessMessage("Configuration is valid.");
      }
    } catch (error) {
      setErrorMessage(error.message || "Validation failed.");
    }
  };

  const handleSave = async () => {
    if (!canManage) return;
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    setValidationErrors([]);
    setValidationWarnings([]);
    try {
      const payload = parsePayload();
      const body = await updateAdminClientViewTransportConfig(clientViewId, payload);
      setEffectiveConfig(body.effectiveConfig || null);
      setValidationWarnings(body.warnings || []);
      setSuccessMessage("Transport configuration saved.");
      await loadConfig();
    } catch (error) {
      setErrorMessage(error.message || "Unable to save Transport configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!canManage) return;
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const body = await resetAdminClientViewTransportConfig(clientViewId);
      setEffectiveConfig(body.effectiveConfig || null);
      setSuccessMessage(body.message || "Transport configuration reset.");
      await loadConfig();
    } catch (error) {
      setErrorMessage(error.message || "Unable to reset Transport configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="panel-card client-view-detail-page__section">
      <h2>Transport configuration</h2>
      <p className="muted">
        Configure Transport layers, sources, dashboard KPIs, and scorecard profiles for this Client
        View. Source types cannot be relabeled as official; mandatory attribution cannot be
        suppressed.
      </p>

      {isLoading ? <p className="muted">Loading Transport configuration…</p> : null}
      {errorMessage ? <p className="error-text">{errorMessage}</p> : null}
      {successMessage ? <p className="muted">{successMessage}</p> : null}

      {effectiveConfig ? (
        <div className="crm-stat-grid" style={{ marginBottom: "1rem" }}>
          <div className="crm-stat">
            <div className="crm-stat-value">{effectiveConfig.enabledLayerGroups?.length ?? 0}</div>
            <div className="crm-stat-label">Enabled layer groups</div>
          </div>
          <div className="crm-stat">
            <div className="crm-stat-value">{effectiveConfig.enabledDashboardCards?.length ?? 0}</div>
            <div className="crm-stat-label">Dashboard cards</div>
          </div>
          <div className="crm-stat">
            <div className="crm-stat-value">{effectiveConfig.scorecardProfile?.profileKey || "—"}</div>
            <div className="crm-stat-label">Scorecard profile</div>
          </div>
        </div>
      ) : null}

      {exampleOptions.length ? (
        <div className="admin-users-field" style={{ marginBottom: "1rem" }}>
          <span>Load example preset</span>
          <div className="crm-actions">
            {exampleOptions.map(({ key }) => (
              <button
                key={key}
                type="button"
                className="crm-btn crm-btn-secondary"
                disabled={!canManage}
                onClick={() => applyExample(key)}
              >
                {key.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <label className="admin-users-field">
        <span>Transport enabled</span>
        <input
          type="checkbox"
          checked={form.isEnabled}
          disabled={!canManage}
          onChange={(event) => setForm((prev) => ({ ...prev, isEnabled: event.target.checked }))}
        />
      </label>

      <label className="admin-users-field">
        <span>Default mode</span>
        <select
          value={form.defaultMode}
          disabled={!canManage}
          onChange={(event) => setForm((prev) => ({ ...prev, defaultMode: event.target.value }))}
        >
          {(schema?.modes || ["network", "access", "disruption", "scorecards", "roads", "live"]).map(
            (mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            )
          )}
        </select>
      </label>

      <label className="admin-users-field">
        <span>Layer config (JSON)</span>
        <textarea
          rows={8}
          value={form.layerConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) => setForm((prev) => ({ ...prev, layerConfigJson: event.target.value }))}
        />
      </label>

      <label className="admin-users-field">
        <span>Source trust config (JSON)</span>
        <textarea
          rows={6}
          value={form.sourceConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) => setForm((prev) => ({ ...prev, sourceConfigJson: event.target.value }))}
        />
      </label>

      <label className="admin-users-field">
        <span>Dashboard cards (JSON)</span>
        <textarea
          rows={6}
          value={form.dashboardConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, dashboardConfigJson: event.target.value }))
          }
        />
      </label>

      <label className="admin-users-field">
        <span>Scorecard profile (JSON)</span>
        <textarea
          rows={3}
          value={form.scorecardConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, scorecardConfigJson: event.target.value }))
          }
        />
      </label>

      <label className="admin-users-field">
        <span>Real Estate access display (JSON)</span>
        <textarea
          rows={4}
          value={form.accessScoreConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, accessScoreConfigJson: event.target.value }))
          }
        />
      </label>

      <label className="admin-users-field">
        <span>Community reports (JSON)</span>
        <textarea
          rows={4}
          value={form.reportConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) => setForm((prev) => ({ ...prev, reportConfigJson: event.target.value }))}
        />
      </label>

      <label className="admin-users-field">
        <span>Live operations (JSON)</span>
        <textarea
          rows={4}
          value={form.liveConfigJson}
          disabled={!canManage}
          spellCheck={false}
          onChange={(event) => setForm((prev) => ({ ...prev, liveConfigJson: event.target.value }))}
        />
      </label>

      {validationErrors.length ? (
        <div className="panel-card" style={{ marginTop: "1rem" }}>
          <h3>Validation errors</h3>
          <ul>
            {validationErrors.map((item, index) => (
              <li key={`${item.field}-${index}`}>
                <StatusBadge tone="danger">Reject</StatusBadge> {item.field}: {item.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {validationWarnings.length ? (
        <div className="panel-card" style={{ marginTop: "1rem" }}>
          <h3>Validation warnings</h3>
          <ul>
            {validationWarnings.map((item, index) => (
              <li key={`${item.field || item.sourceKey}-${index}`}>
                <StatusBadge tone="warning">Warn</StatusBadge>{" "}
                {item.field || item.sourceKey || "config"}: {item.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {canManage ? (
        <div className="crm-actions" style={{ marginTop: "1rem" }}>
          <button type="button" className="crm-btn crm-btn-secondary" onClick={handleValidate}>
            Validate
          </button>
          <button
            type="button"
            className="crm-btn crm-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Save Transport config"}
          </button>
          <button
            type="button"
            className="crm-btn crm-btn-secondary"
            onClick={handleReset}
            disabled={isSaving}
          >
            Reset to defaults
          </button>
        </div>
      ) : (
        <p className="muted">Managing Transport configuration requires client_views.manage.</p>
      )}
    </section>
  );
}

export default ClientViewTransportConfigSection;
